import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './CreateFolderModal.css';

const CreateFolderModal = ({ isOpen, onClose, onCreateFolder, parentFolder = null, tenantId }) => {
  const [folderName, setFolderName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    if (!tenantId) {
      setError('Tenant ID is required');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await onCreateFolder({
        name: folderName.trim(),
        description: description.trim(),
        parentFolderId: parentFolder?.folderId || null,
        color,
      });
      
      // Reset form and close modal
      setFolderName('');
      setDescription('');
      setColor('#3B82F6');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create folder');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setFolderName('');
      setDescription('');
      setColor('#3B82F6');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Predefined color palette
  const colorPalette = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#14B8A6', // Teal
    '#F97316', // Deep Orange
    '#6366F1', // Indigo
    '#84CC16', // Lime
    '#06B6D4', // Cyan
    '#F43F5E', // Rose
  ];

  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content create-folder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Folder</h2>
          <button 
            className="modal-close-btn" 
            onClick={handleClose}
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="folder-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {parentFolder && (
            <div className="parent-folder-info">
              <span className="info-label">Parent folder:</span>
              <span className="parent-folder-name">{parentFolder.name}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="folderName">
              Folder Name <span className="required">*</span>
            </label>
            <input
              id="folderName"
              type="text"
              className="form-input"
              placeholder="e.g., Frontend Projects"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              disabled={isCreating}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="form-textarea"
              placeholder="Optional description for this folder"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isCreating}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Folder Color</label>
            <div className="color-palette">
              {colorPalette.map((paletteColor) => (
                <button
                  key={paletteColor}
                  type="button"
                  className={`color-option ${color === paletteColor ? 'selected' : ''}`}
                  style={{ backgroundColor: paletteColor }}
                  onClick={() => setColor(paletteColor)}
                  disabled={isCreating}
                  title={paletteColor}
                />
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isCreating || !folderName.trim()}
            >
              {isCreating ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

CreateFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateFolder: PropTypes.func.isRequired,
  parentFolder: PropTypes.shape({
    folderId: PropTypes.string,
    name: PropTypes.string,
  }),
  tenantId: PropTypes.string.isRequired,
};

export default CreateFolderModal;
